/**
 * Akt-Hub Dokument-Zuordnung — gemeinsame, testbare Invarianten.
 *
 * AKTE_DOC_DND_OR_SKIP:
 *   VERBOTEN: externer Drop ohne sichtbare Ziel-Akte, still geschluckte Datei,
 *   Akt-Tab listet Pool-/Kanzlei-Wurzel.
 *   ERLAUBT: Nicht-Audio-Datei mit expliziter `akte_id`; DND auf die Akt-Fläche = Kopie
 *   (Quelle bleibt); nur Dokumente dieser Akte im Tab.
 *
 * AKTE_DOC_MOVE_OR_ERROR:
 *   VERBOTEN: NC-MOVE als Erfolg zeigen, obwohl PB-Pfad/`akte_id` nicht nachgezogen wurden;
 *   Kopieren löscht/verschiebt die Quelle oder setzt `akte_id` an der Pool-Zeile.
 *   ERLAUBT: Einfügen = NC-MOVE + PB-PATCH; Kopieren = NC-COPY + neue PB-Zeile.
 */

export const PORTAL_AKTE_DOC_DND_CLASS = 'AKTE_DOC_DND_OR_SKIP' as const;
export const PORTAL_AKTE_DOC_MOVE_CLASS = 'AKTE_DOC_MOVE_OR_ERROR' as const;

/** Nur Smoke-Skript — niemals Produkt-Move/Create-Ziel. */
export const PORTAL_AKTE_SMOKE_FOLDER_NAME = 'uat-move-smoke' as const;

export function portalFolderSegmentIsSmokeFixture(segment: string): boolean {
  const s = segment.trim();
  return s === PORTAL_AKTE_SMOKE_FOLDER_NAME || /^[._]*arkorbit-tmp-move-[a-z0-9-]+$/i.test(s);
}

export function portalFolderPathContainsSmokeFixture(path?: string | null): boolean {
  return String(path ?? '')
    .split(/[\\/]+/)
    .some((seg) => portalFolderSegmentIsSmokeFixture(seg));
}

export function normalizePortalDocumentNcPath(path?: string | null): string {
  const clean = String(path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  return clean ? `/${clean}` : '';
}

export function portalAkteTargetFolder(akteId?: string | null): string {
  const id = String(akteId ?? '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
  return id ? `akten/${id}` : '';
}

/** Produkt-Move/Ingest: immer `akten/<id>`, nie Rest-Unterordner aus Smoke/nc_path. */
export function portalAkteAssignTargetFolder(akteId?: string | null): string {
  return portalAkteTargetFolder(akteId);
}

/** Relativ zu `/akten/<id>/` — leer = Aktenwurzel. */
export function portalAkteRelativeDocFolder(
  nextcloudPath?: string | null,
  akteId?: string | null,
): string {
  const normalized = normalizePortalDocumentNcPath(nextcloudPath);
  const target = portalAkteTargetFolder(akteId);
  if (!normalized || !target) return '';
  const marker = `/${target}/`;
  const index = normalized.toLowerCase().indexOf(marker.toLowerCase());
  if (index < 0) return '';
  const parts = normalized
    .slice(index + marker.length)
    .split('/')
    .filter(Boolean);
  if (parts.length <= 1) return '';
  parts.pop();
  return parts.join('/');
}

/** NC-Listenpfad auf den Akten-Explorer-Raum (ohne Mandanten-/akten/<id>-Prefix). */
export function portalFolderRelativeToAkte(folderPath: string, akteId: string): string {
  const id = String(akteId ?? '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
  const clean = folderPath.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  if (!id || !clean || portalFolderPathContainsSmokeFixture(clean)) return '';
  const marker = `akten/${id}`;
  const lower = clean.toLowerCase();
  const markerLower = marker.toLowerCase();
  if (lower === markerLower) return '';
  const prefixed = lower.indexOf(`${markerLower}/`);
  if (prefixed >= 0) return clean.slice(prefixed + marker.length + 1);
  if (/(^|\/)akten\//i.test(clean) || /^mandanten/i.test(clean)) return '';
  return clean;
}

/** Folder-API: explizite `akte_id` sticht; sonst `akten/<id>` irgendwo im Pfad. */
export function portalFolderApiAkteId(
  folderPath?: string | null,
  explicitAkteId?: string | null,
): string {
  const explicit = String(explicitAkteId ?? '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 20);
  if (explicit) return explicit;
  const match = String(folderPath ?? '')
    .replace(/\\/g, '/')
    .match(/(?:^|\/)akten\/([a-zA-Z0-9]{10,20})(?:\/|$)/);
  return match?.[1] ?? '';
}

/** Kanonischer voller Aktenpfad aus dem vom Server bestaetigten Dokumentpfad. */
export function portalAkteNcPathFromMovedDocument(
  documentNcPath?: string | null,
  akteId?: string | null,
): string | null {
  const normalized = normalizePortalDocumentNcPath(documentNcPath);
  const target = portalAkteTargetFolder(akteId);
  if (!normalized || !target) return null;
  const marker = `/${target}/`;
  const index = normalized.toLowerCase().indexOf(marker.toLowerCase());
  if (index < 0) return null;
  return `${normalized.slice(0, index + marker.length).replace(/\/+$/, '')}/`;
}

export function portalDocumentMovePersistedInAkte(
  record: { id?: string | null; akte_id?: string | null; nextcloud_path?: string | null },
  akteId: string,
  expectedDocumentPath?: string | null,
): boolean {
  const id = akteId.trim();
  if (!record.id?.trim() || !id || record.akte_id?.trim() !== id) return false;
  const actualPath = normalizePortalDocumentNcPath(record.nextcloud_path);
  if (!actualPath || !portalAkteNcPathFromMovedDocument(actualPath, id)) return false;
  if (portalFolderPathContainsSmokeFixture(actualPath)) return false;
  const expectedPath = normalizePortalDocumentNcPath(expectedDocumentPath);
  return !expectedPath || actualPath === expectedPath;
}

const PORTAL_AKTE_KANZLEI_ROOT_FIRST = /^(akten|sitze|mandanten|portal-documents)$/i;

export function portalAkteFolderIsKanzleiRoot(folder?: string | null): boolean {
  const first = String(folder ?? '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .split('/')[0];
  return Boolean(first) && PORTAL_AKTE_KANZLEI_ROOT_FIRST.test(first);
}

/** Ordner nur unter `/akten/<id>/` — Pool-/Kanzlei-Wurzel wird verworfen. */
export function collectPortalAkteExplorerFolders(input: {
  akteId: string;
  documents?: { nextcloud_path?: string | null }[];
  listedFolders?: string[];
}): string[] {
  const akteId = String(input.akteId ?? '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 20);
  const folders = new Set<string>();
  const addWithParents = (raw: string) => {
    const clean = String(raw || '')
      .replace(/\\/g, '/')
      .replace(/^\/+|\/+$/g, '');
    if (!clean || portalFolderPathContainsSmokeFixture(clean)) return;
    if (portalAkteFolderIsKanzleiRoot(clean)) return;
    let acc = '';
    for (const part of clean.split('/').filter(Boolean)) {
      acc = acc ? `${acc}/${part}` : part;
      if (!portalAkteFolderIsKanzleiRoot(acc)) folders.add(acc);
    }
  };

  for (const doc of input.documents ?? []) {
    addWithParents(portalAkteRelativeDocFolder(doc.nextcloud_path, akteId));
  }
  for (const listed of input.listedFolders ?? []) {
    const raw = String(listed || '').replace(/\\/g, '/');
    if (!akteId) continue;
    const hasMarker = new RegExp(`(?:^|/)akten/${akteId}(?:/|$)`, 'i').test(raw);
    if (!hasMarker) continue;
    const rel = portalFolderRelativeToAkte(raw, akteId);
    if (rel) addWithParents(rel);
  }
  return Array.from(folders).sort((a, b) => a.localeCompare(b, 'de'));
}

export function portalAkteExplorerChildFolders(
  allFolders: string[],
  currentFolder = '',
): string[] {
  const cwd = String(currentFolder || '')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '');
  const prefix = cwd ? `${cwd}/` : '';
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of allFolders) {
    const folder = String(raw || '')
      .replace(/\\/g, '/')
      .replace(/^\/+|\/+$/g, '');
    if (!folder || folder === cwd) continue;
    if (cwd && !folder.startsWith(prefix)) continue;
    const rest = cwd ? folder.slice(prefix.length) : folder;
    const first = rest.split('/').filter(Boolean)[0];
    if (!first) continue;
    const childPath = cwd ? `${cwd}/${first}` : first;
    if (seen.has(childPath) || portalAkteFolderIsKanzleiRoot(childPath)) continue;
    seen.add(childPath);
    out.push(childPath);
  }
  return out.sort((a, b) => a.localeCompare(b, 'de'));
}

/** Kopie: neue PB-Zeile in der Akte, Quelle unverändert (keine Ziel-`akte_id`). */
export function portalDocumentCopyKeepsSource(args: {
  sourceBefore: { id?: string | null; akte_id?: string | null; nextcloud_path?: string | null };
  sourceAfter: { id?: string | null; akte_id?: string | null; nextcloud_path?: string | null };
  copy: { id?: string | null; akte_id?: string | null; nextcloud_path?: string | null };
  targetAkteId: string;
}): boolean {
  const target = args.targetAkteId.trim();
  const srcId = args.sourceBefore.id?.trim() || '';
  if (!srcId || !target || args.sourceAfter.id?.trim() !== srcId) return false;
  if (
    normalizePortalDocumentNcPath(args.sourceAfter.nextcloud_path) !==
    normalizePortalDocumentNcPath(args.sourceBefore.nextcloud_path)
  ) {
    return false;
  }
  const sourceAkteBefore = args.sourceBefore.akte_id?.trim() || '';
  const sourceAkteAfter = args.sourceAfter.akte_id?.trim() || '';
  if (sourceAkteAfter !== sourceAkteBefore) return false;
  if (sourceAkteAfter === target) return false;
  const copyId = args.copy.id?.trim() || '';
  if (!copyId || copyId === srcId) return false;
  return portalDocumentMovePersistedInAkte(args.copy, target);
}

export function portalDocumentCutPersisted(
  record: { id?: string | null; akte_id?: string | null; nextcloud_path?: string | null },
  sourceId: string,
  targetAkteId: string,
): boolean {
  if (!sourceId.trim() || record.id?.trim() !== sourceId.trim()) return false;
  return portalDocumentMovePersistedInAkte(record, targetAkteId);
}
